import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Stack,
  Autocomplete,
  Paper,
  Card,
  CardContent,
  Grid,
  Chip,
  Tooltip,
  CircularProgress,
  Divider,
} from "@mui/material";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import EditOutlined from "@mui/icons-material/EditOutlined";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Close from "@mui/icons-material/Close";
import { createAxiosInstance } from "../../../api";
import { useDispatch, useSelector } from "react-redux";
import { getMyProfile } from "../../../store/setProfile";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { dispatchProfileEvent } from "../hooks/useProfileEvents";
// @ts-ignore - SVG import
import errorIcon from "../../../assets/error-icon.svg";

const examBoards = ["AQA", "OCR", "Edexcel", "WJEC", "CCEA", "Other"];

type SubjectData = {
  name: string;
  levels: string[];
};

type Row = {
  _id?: string;
  subject: string;
  level: string;
  board?: string;
  price?: string;
  saved?: boolean;
  isRowDisabled?: boolean;
  editing?: boolean;
};

export default function Subjects({ onDirtyChange, onValidChange, onSave }: any) {
  const dispatch = useDispatch();
  const profileUser = useSelector((state: any) => state.profile?.user);
  const { status: profileStatus } = profileUser || {};
  const { status: accountStatus } = useSelector((state: any) => state.main) || {};

  let userId: string;
  try {
    const persistedData = localStorage.getItem("persist:root");
    const userData = JSON.parse(persistedData || "{}");
    if (userData.userId && typeof userData.userId === "string") {
      userData.userId = userData.userId.replace(/\\/g, "");
    }
    userId = userData?.userId?.replace(/"/g, "") || "";
  } catch (error) {
    console.error("Error parsing localStorage data:", error);
    userId = "";
  }

  const [subjectsData, setSubjectsData] = useState<SubjectData[]>([]);
  const [subjects, setSubjects] = useState<string[]>([]);
  const [savedRows, setSavedRows] = useState<Row[]>([]);
  const [draftRow, setDraftRow] = useState<Row>({
    subject: "",
    level: "",
    board: "",
    price: "",
    saved: false,
  });
  // Boards are multi-select in the Add row (one saved row per selected board).
  // The single `board` field on Row / draftRow stays a string because saved
  // rows and the edit flow are still one row = one board.
  const [draftBoards, setDraftBoards] = useState<string[]>([]);
  const [draftLevels, setDraftLevels] = useState<string[]>([]);
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editDataMap, setEditDataMap] = useState<Record<string, Row>>({});
  const [editLevelsMap, setEditLevelsMap] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(false);
  const [isFetchingSubjects, setIsFetchingSubjects] = useState(false);

  const fetchSavedRows = useCallback(async () => {
    if (!userId) return;
    setIsFetchingSubjects(true);
    try {
      const res = await createAxiosInstance().get(
        `/api/subjects/user/${userId}`
      );
      const saved = (res.data || []).map((s: any) => ({
        _id: s._id,
        subject: s.subject || "",
        level: s.level || "",
        board: s.board || "",
        price: String(s.price || ""),
        saved: true,
        isRowDisabled: true,
      }));
      setSavedRows(saved);
    } catch (err) {
      console.error("Failed to fetch subjects:", err);
    } finally {
      setIsFetchingSubjects(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchSavedRows();
    createAxiosInstance()
      .get("/api/subjects?limit=1000")
      .then((res) => {
        const subjectsArray = res.data?.subjects || [];
        // Store full subject data with levels
        setSubjectsData(subjectsArray.map((s: any) => ({
          name: s.name || s.subject || "",
          levels: s.levels || []
        })));
        // Store just names for autocomplete
        const subs = subjectsArray.map((s: any) => s.name || s.subject || "");
        setSubjects(subs);
      })
      .catch((err) => console.error("Failed to fetch subject list:", err));
  }, [fetchSavedRows]);

  // Get levels for a specific subject
  const getLevelsForSubject = (subjectName: string): string[] => {
    const subjectData = subjectsData.find(s => s.name === subjectName);
    return subjectData?.levels || [];
  };

  const handleSaveNew = async () => {
    if (!draftRow.subject?.trim() || !draftRow.price?.trim()) {
      toast.error("Subject and price are required!");
      return;
    }

    const isProfileLive = profileStatus === 1 && accountStatus === 1;
    if (isProfileLive) {
      const confirm = await Swal.fire({
        title: "Save Subject",
        text: "This will move your profile to draft status. Continue?",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#712d94",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        cancelButtonText: "Cancel",
      });
      if (!confirm.isConfirmed) return;
    }

    // Fan out one subject per selected exam board. If no boards were picked
    // we still save a single row with board="" so the "board is optional"
    // path keeps working. The API dedupes on (subject, level, board), so a
    // tutor adding Physics A-Level for AQA / OCR / Edexcel / WJEC in one
    // click gets four separate saved rows.
    const boardsToSave = draftBoards.length > 0 ? draftBoards : [""];
    const subjectsPayload = boardsToSave.map((board) => ({
      subject: draftRow.subject,
      level: draftRow.level || "",
      board,
      price: draftRow.price,
    }));

    setLoading(true);
    try {
      await createAxiosInstance().post(`/api/subjects/save/${userId}`, {
        subjects: subjectsPayload,
      });
      toast.success(
        subjectsPayload.length > 1
          ? `${subjectsPayload.length} subjects saved successfully.`
          : "Subject saved successfully."
      );
      (dispatch as any)(getMyProfile());
      setDraftRow({ subject: "", level: "", board: "", price: "", saved: false });
      setDraftBoards([]);
      fetchSavedRows();
      onDirtyChange?.(false);
      // Notify other components that subjects have been updated
      dispatchProfileEvent('subjectsUpdated');
    } catch (err: any) {
      Swal.fire({
        title: "Error",
        text: err?.response?.data?.message || "Failed to save subject.",
        imageUrl: errorIcon,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (rowId: string, updatedData: Row) => {
    if (!updatedData.subject?.trim() || !updatedData.price?.trim()) {
      toast.error("Subject and price are required!");
      return;
    }

    const isProfileLive = profileStatus === 1 && accountStatus === 1;
    if (isProfileLive) {
      const confirm = await Swal.fire({
        title: "Update Subject",
        text: "This will move your profile to draft status. Continue?",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#712d94",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        cancelButtonText: "Cancel",
      });
      if (!confirm.isConfirmed) return;
    }

    setLoading(true);
    try {
      await createAxiosInstance().put(
        `/api/subjects/update/${userId}/${rowId}`,
        {
          subject: updatedData.subject,
          level: updatedData.level || "",
          board: updatedData.board || "",
          price: updatedData.price,
        }
      );
      toast.success("Subject updated successfully.");
      (dispatch as any)(getMyProfile());
      setEditingRow(null);
      fetchSavedRows();
      onDirtyChange?.(false);
      // Notify other components that subjects have been updated
      dispatchProfileEvent('subjectsUpdated');
    } catch (err: any) {
      Swal.fire({
        title: "Error",
        text: err?.response?.data?.message || "Failed to update subject.",
        imageUrl: errorIcon,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (rowId: string, subjectName: string) => {
    const confirm = await Swal.fire({
      title: "Delete Subject",
      text: `Are you sure you want to delete "${subjectName}"?`,
      icon: "warning",
      iconColor: "#d32f2f",
      showCancelButton: true,
      confirmButtonText: "Yes",
      confirmButtonColor: "#712d94",
      cancelButtonColor: "#d32f2f",
    });
    if (!confirm.isConfirmed) return;

    setLoading(true);
    try {
      await createAxiosInstance().delete(`/api/subjects/delete/${userId}/${rowId}`);
      toast.success("Subject deleted successfully.");
      (dispatch as any)(getMyProfile());
      fetchSavedRows();
      onDirtyChange?.(false);
      // Notify other components that subjects have been updated
      dispatchProfileEvent('subjectsUpdated');
    } catch (err: any) {
      Swal.fire({
        title: "Error",
        text: "Failed to delete subject.",
        imageUrl: errorIcon,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Notify parent of validation status
    const isValid = savedRows.length > 0;
    onValidChange?.(isValid);
  }, [savedRows.length, onValidChange]);

  (Subjects as any).save = async () => {
    if (draftRow.subject && draftRow.price) {
      await handleSaveNew();
    }
  };

  return (
    <Box>
      <Typography 
        variant="h2"
        sx={{ 
          fontWeight: 700,
        }}
      >
        <Box sx={{ color: '#712d94 !important' }}>
          What subjects can you teach?  
        </Box>
        
      </Typography>
      <Box 
        sx={{ 
          '&': {
            marginBottom: '1.5rem !important'
          }
        }}
      >
        <Typography variant="body2" sx={{ color: "#6b7280" }}>
          (Subject and price are required)
        </Typography>
      </Box>

      {/* Add New Subject Card */}
      <Card
        elevation={0}
        sx={{
          border: "1px solid #e9e9ec",
          borderRadius: 2,
          mb: 2,
          backgroundColor: "#fafafa",
        }}
      >
        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <AddCircleOutline color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
              Add New Subject
            </Typography>
          </Box>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={subjects}
                value={draftRow.subject}
                onChange={(_, val) => {
                  if (val) {
                    const levels = getLevelsForSubject(val);
                    setDraftLevels(levels);
                    setDraftRow({ ...draftRow, subject: val, level: "" });
                  } else {
                    setDraftLevels([]);
                    setDraftRow({ ...draftRow, subject: "", level: "" });
                  }
                }}
                filterOptions={(options, { inputValue }) => {
                  const term = inputValue.toLowerCase();
                  return options.filter((option) => {
                    const lower = option.toLowerCase();
                    if (lower.includes(term)) return true;
                    // Strip non-alphanumeric chars so "11" matches "(+11)" or "(11+)"
                    const stripped = lower.replace(/[^a-z0-9 ]/g, '');
                    if (stripped.includes(term)) return true;
                    return false;
                  });
                }}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Subject *"
                    placeholder="Select subject"
                    error={!draftRow.subject && draftRow.price ? true : false}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={2.5}>
              <Autocomplete
                options={draftLevels}
                value={draftRow.level}
                onChange={(_, val) => setDraftRow({ ...draftRow, level: val || "" })}
                size="small"
                disabled={!draftRow.subject || draftLevels.length === 0}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Level" 
                    placeholder={draftLevels.length === 0 && draftRow.subject ? "No levels needed" : "Select level"} 
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={2.5}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={examBoards}
                value={draftBoards}
                onChange={(_, val) => setDraftBoards(val)}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Exam Boards"
                    placeholder={draftBoards.length === 0 ? "Select one or more" : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Price (£/hr) *"
                value={draftRow.price || ""}
                onChange={(e) => setDraftRow({ ...draftRow, price: e.target.value })}
                placeholder="0.00"
                type="number"
                error={!draftRow.price && draftRow.subject ? true : false}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSaveNew}
                disabled={loading || !draftRow.subject || !draftRow.price}
                sx={{ height: "40px" }}
              >
                {loading ? <CircularProgress size={20} /> : "Add Subject"}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Saved Subjects */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Your Subjects ({savedRows.length})
        </Typography>

        {isFetchingSubjects ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : savedRows.length === 0 ? (
          <Card
            elevation={0}
            sx={{
              border: "1px solid #e9e9ec",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              backgroundColor: "#fafafa",
            }}
          >
            <Typography variant="body1" color="text.secondary">
              No subjects added yet. Use the form above to add your first subject.
            </Typography>
          </Card>
        ) : (
          <Grid container spacing={2} sx={{ width: "100%" }}>
            {savedRows.map((row) => {
              const isEditing = editingRow === row._id;
              const editData = editDataMap[row._id!] || row;

              return (
                <Grid item xs={12} key={row._id}>
                  <Card
                    elevation={0}
                    sx={{
                      width: "100%",
                      border: "1px solid #e9e9ec",
                      borderRadius: 2,
                      "&:hover": {
                        borderColor: "#712d94",
                        boxShadow: "0 2px 8px rgba(113, 45, 148, 0.1)",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    <CardContent sx={{ width: "100%", p: 2.5 }}>
                      {isEditing ? (
                        <Grid container spacing={2} alignItems="center" sx={{ width: "100%" }}>
                          <Grid item xs={12} sm={12} md={3.5}>
                            <Autocomplete
                              options={subjects}
                              value={editData.subject}
                              onChange={(_, val) => {
                                if (val) {
                                  const levels = getLevelsForSubject(val);
                                  setEditLevelsMap({ ...editLevelsMap, [row._id!]: levels });
                                  setEditDataMap({ ...editDataMap, [row._id!]: { ...editData, subject: val, level: "" } });
                                } else {
                                  setEditLevelsMap({ ...editLevelsMap, [row._id!]: [] });
                                  setEditDataMap({ ...editDataMap, [row._id!]: { ...editData, subject: "", level: "" } });
                                }
                              }}
                              size="small"
                              fullWidth
                              renderInput={(params) => (
                                <TextField {...params} label="Subject *" fullWidth />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={2.5}>
                            <Autocomplete
                              options={editLevelsMap[row._id!] || getLevelsForSubject(editData.subject)}
                              value={editData.level}
                              onChange={(_, val) =>
                                setEditDataMap({ ...editDataMap, [row._id!]: { ...editData, level: val || "" } })
                              }
                              size="small"
                              fullWidth
                              disabled={!editData.subject || (editLevelsMap[row._id!] || getLevelsForSubject(editData.subject)).length === 0}
                              renderInput={(params) => (
                                <TextField 
                                  {...params} 
                                  label="Level" 
                                  fullWidth 
                                  placeholder={(editLevelsMap[row._id!] || getLevelsForSubject(editData.subject)).length === 0 && editData.subject ? "No levels needed" : "Select level"}
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={2.5}>
                            <Autocomplete
                              options={examBoards}
                              value={editData.board || ""}
                              onChange={(_, val) =>
                                setEditDataMap({ ...editDataMap, [row._id!]: { ...editData, board: val || "" } })
                              }
                              size="small"
                              fullWidth
                              renderInput={(params) => (
                                <TextField {...params} label="Exam Board" fullWidth />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={2}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Price (£/hr) *"
                              value={editData.price || ""}
                              onChange={(e) =>
                                setEditDataMap({ ...editDataMap, [row._id!]: { ...editData, price: e.target.value } })
                              }
                              type="number"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={1.5}>
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <Tooltip title="Save changes">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleUpdate(row._id!, editData)}
                                  disabled={loading || !editData.subject || !editData.price}
                                  sx={{
                                    border: "1px solid #712d94",
                                    backgroundColor: "rgba(113, 45, 148, 0.08)",
                                    "&:hover": {
                                      backgroundColor: "rgba(113, 45, 148, 0.15)",
                                    },
                                  }}
                                >
                                  {loading ? (
                                    <CircularProgress size={18} />
                                  ) : (
                                    <CheckCircle fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditingRow(null);
                                    const newMap = { ...editDataMap };
                                    delete newMap[row._id!];
                                    setEditDataMap(newMap);
                                  }}
                                  disabled={loading}
                                  sx={{
                                    border: "1px solid #e0e0e0",
                                    "&:hover": {
                                      borderColor: "#d32f2f",
                                      color: "#d32f2f",
                                    },
                                  }}
                                >
                                  <Close fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Grid>
                        </Grid>
                      ) : (
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 2,
                            }}
                          >
                            <Box>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {row.subject}
                                </Typography>
                                <Chip
                                  icon={<CheckCircleOutline fontSize="small" />}
                                  label="Saved"
                                  color="success"
                                  size="small"
                                />
                              </Box>
                              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                                {row.level && (
                                  <Chip label={`Level: ${row.level}`} size="small" variant="outlined" />
                                )}
                                {row.board && (
                                  <Chip label={`Board: ${row.board}`} size="small" variant="outlined" />
                                )}
                                <Chip
                                  label={`£${row.price}/hr`}
                                  size="small"
                                  color="primary"
                                  sx={{ fontWeight: 600 }}
                                />
                              </Stack>
                            </Box>
                            <Stack direction="row" spacing={1}>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditingRow(row._id!);
                                    setEditDataMap({ ...editDataMap, [row._id!]: { ...row } });
                                  }}
                                  sx={{ border: "1px solid #e0e0e0" }}
                                >
                                  <EditOutlined fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(row._id!, row.subject)}
                                  sx={{ border: "1px solid #e0e0e0" }}
                                  color="error"
                                >
                                  <DeleteOutline fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </Box>
  );
}
